import React, { useState, useEffect, useRef } from 'react';
import { User, Menu, ChevronDown, Monitor, ShieldCheck, Clock, Palette } from 'lucide-react';
import Cookies from 'js-cookie';
import ThemeSelector from './theme/ThemeSelector';
import './Header.css';

const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const Header = ({ toggleMobileMenu }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showThemePopover, setShowThemePopover] = useState(false);
  const dropdownRef = useRef(null);
  const themeRef = useRef(null);

  const licenseLimit = parseInt(Cookies.get('licenseLimit') || '.0', 10);
  const subscriptionExpiry = Cookies.get('subscriptionExpiry') || new Date().toISOString();
  const [used, setUsed] = useState(0);

  useEffect(() => {
    if (showDropdown) {
      setUsed(parseInt(localStorage.getItem('totalNodesEnrolled') || '0', 10));
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Ignore click-outside if target is inside the Custom Theme Studio Color Designer portal
      if (e.target && e.target.closest && (e.target.closest('.studio-color-designer-portal') || e.target.closest('[data-portal="theme-portal"]'))) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (themeRef.current && !themeRef.current.contains(e.target)) {
        setShowThemePopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="header glass" style={{ padding: '0 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%', borderBottom: '1px solid var(--border-light)', background: 'var(--bg-header)' }}>
      <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <button className="mobile-menu-btn" onClick={toggleMobileMenu} style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', display: 'none', padding: 4 }}>
          <Menu size={24} />
        </button>
        <h1 className="dash-title" style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Organization Overview</h1>
        <div className="live-status" style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(34,197,94,0.12)', padding: '4px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700, color: '#22C55E', letterSpacing: '0.05em' }}>
          <span className="pulse-dot anim-blink" style={{ width: 6, height: 6, borderRadius: '50%', background: '#22C55E', boxShadow: '0 0 8px #22C55E' }}></span>
          LIVE
        </div>
      </div>

      <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Theme Selector Icon Button */}
        <div ref={themeRef} style={{ position: 'relative' }}>
          <button
            onClick={() => { setShowThemePopover(!showThemePopover); setShowDropdown(false); }}
            title="Theme Settings"
            style={{
              background: 'rgba(var(--app-primary-color-rgb), 0.12)',
              border: '1px solid var(--border-light)',
              color: 'var(--accent-primary)',
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <Palette size={18} />
          </button>

          {showThemePopover && (
            <div style={{
              position: 'absolute',
              top: '125%',
              right: 0,
              width: 340,
              background: 'var(--bg-card)',
              border: '1px solid var(--border-light)',
              borderRadius: 16,
              boxShadow: '0 15px 40px rgba(0,0,0,0.3)',
              zIndex: 1000,
              overflow: 'hidden'
            }} onClick={e => e.stopPropagation()}>
              <ThemeSelector onItemClick={() => setShowThemePopover(false)} />
            </div>
          )}
        </div>

        {/* Profile Dropdown Button */}
        <div
          ref={dropdownRef}
          className="profile-btn"
          onClick={() => { setShowDropdown(!showDropdown); setShowThemePopover(false); }}
          style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, padding: '4px 12px 4px 4px', borderRadius: 24, background: 'var(--bg-card)', border: '1px solid var(--border-light)', cursor: 'pointer', marginLeft: 4 }}
        >
          <div className="avatar" style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(var(--app-primary-color-rgb), 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <User size={16} color="var(--accent-primary)" />
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{Cookies.get('adminName') || Cookies.get('adminUsername') || 'Admin'}</span>
          <ChevronDown size={14} color="var(--text-secondary)" style={{ transform: showDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />

          {showDropdown && (
            <div style={{
              position: 'absolute',
              top: '120%',
              right: 0,
              width: 260,
              background: 'var(--bg-card)',
              border: '1px solid var(--border-light)',
              borderRadius: 12,
              padding: 16,
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              zIndex: 100,
              cursor: 'default'
            }} onClick={e => e.stopPropagation()}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-light)', paddingBottom: 10 }}>License Information</h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Monitor size={16} color="#22C55E" /></div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Licenses Used</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{used} / {licenseLimit} PCs</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ShieldCheck size={16} color="#3B82F6" /></div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Licenses Remaining</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{Math.max(0, licenseLimit - used)} PCs</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(var(--app-primary-color-rgb),0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Clock size={16} color="var(--accent-primary)" /></div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Subscription Expiry</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{formatDateDDMMYYYY(subscriptionExpiry)}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;